import type { NestedFilter, CommonSort, GQL_ID } from '@@partner:types:graphql'

import React, { useState, useCallback, useEffect } from 'react'
import { useFetchReportTypes, useFetchReportSchedules } from '@@partner:graphql/query/report/reports.gql'
import { 
  useSaveReportSchedule,
  useDeleteReportSchedule,
  useToggleReportSchedule,
  useGenerateReport 
} from '@@partner:graphql/mutation/report/reports.gql'

import { Message, Pagination, Button, Modal, Panel } from 'rsuite'
import ReportSchedulesTable from '@components/report/table/ReportSchedulesTable'
import QuickReportGeneration from '@components/report/QuickReportGeneration'
import ReportScheduleForm from '@components/report/form/ReportScheduleForm'

import { convertToInt } from '@@partner:helpers/util'
import { 
  notifyOk, 
  notifyError,
  makeGraphQLErrorNotification 
} from '@@partner:helpers/notification'

import './ReportsPage.scss'

// Import proper types from our types file
import type { 
  ReportSchedule, 
  ReportType, 
  CreateReportScheduleInput, 
  UpdateReportScheduleInput 
} from '@@partner:types:app'

const REPORTS_PER_PAGE_LIMIT = 20

export default function ReportsPage () {
  const [ page, setPage ] = useState(1)
  const [ filter, setFilter ] = useState<NestedFilter>({
    fields: [ 'id' ],
    op: 'unwrap'
  })
  const [ sort, setSort ] = useState<CommonSort>({
    fieldName: 'id',
    direction: 0
  })
  const [ showCreateModal, setShowCreateModal ] = useState(false)
  const [ editingSchedule, setEditingSchedule ] = useState<ReportSchedule | null>(null)

  // GraphQL queries and mutations
  const { data: reportTypesData, loading: loadingTypes } = useFetchReportTypes()
  const { data: schedulesData, loading: loadingSchedules, error, refetch } = useFetchReportSchedules({
    pager: {
      page,
      limit: REPORTS_PER_PAGE_LIMIT
    },
    filter,
    sort
  })

  const [ saveSchedule, { loading: savingSchedule } ] = useSaveReportSchedule()
  const [ deleteSchedule, { loading: deletingSchedule } ] = useDeleteReportSchedule()
  const [ toggleSchedule ] = useToggleReportSchedule()
  const [ generateReport, { loading: generatingReport } ] = useGenerateReport()

  const handlePageSelect = useCallback((selectedPage: number | string) => {
    setPage(convertToInt(selectedPage))
  }, [])

  const handleSortColumn = useCallback((sortColumn, sortType) => {
    setSort({
      fieldName: sortColumn,
      direction: sortType === 'desc' ? 0 : 1
    })
  }, [])

  const handleCreateSchedule = useCallback(() => {
    setEditingSchedule(null)
    setShowCreateModal(true)
  }, [])

  const handleEditSchedule = useCallback((schedule: ReportSchedule) => {
    setEditingSchedule(schedule)
    setShowCreateModal(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false)
    setEditingSchedule(null)
  }, [])

  const handleSubmitForm = useCallback(async (data: CreateReportScheduleInput | UpdateReportScheduleInput) => {
    try {
      const reportScheduleInfo = {
        ...data,
        id: editingSchedule?.id // Include ID for updates, undefined for new schedules
      }

      await saveSchedule({
        variables: {
          reportScheduleInfo
        }
      })
      
      notifyOk(editingSchedule ? 'Расписание обновлено' : 'Расписание создано')
      handleCloseModal()
      refetch()
    } 
    catch (error) {
      makeGraphQLErrorNotification('Ошибка сохранения расписания')(error as any)
    }
  }, [ editingSchedule, saveSchedule, handleCloseModal, refetch ])

  const handleDeleteSchedule = useCallback(async (scheduleId: GQL_ID) => {
    if (!confirm('Вы уверены, что хотите удалить это расписание?')) return

    try {
      await deleteSchedule({
        variables: { id: scheduleId }
      })
      notifyOk('Расписание удалено')
      refetch()
    } 
    catch (error) {
      makeGraphQLErrorNotification('Ошибка удаления расписания')(error as any)
    }
  }, [ deleteSchedule, refetch ])

  const handleToggleSchedule = useCallback(async (scheduleId: GQL_ID, isEnabled: boolean) => {
    try {
      await toggleSchedule({
        variables: { 
          id: scheduleId
        }
      })
      notifyOk(isEnabled ? 'Расписание активировано' : 'Расписание приостановлено')
      refetch()
    } 
    catch (error) {
      makeGraphQLErrorNotification('Ошибка изменения статуса расписания')(error as any)
    }
  }, [ toggleSchedule, refetch ])

  const handleGenerateReport = useCallback(async (schedule: ReportSchedule) => {
    try {
      const response = await generateReport({
        variables: {
          id: schedule.id
        }
      })

      if (response.data?.runReportSchedule) {
        notifyOk('Отчет успешно сгенерирован и отправлен')
      } else {
        notifyError(response.data?.runReportSchedule?.message || 'Ошибка генерации отчета')
      }
    } 
    catch (error) {
      makeGraphQLErrorNotification('Ошибка генерации отчета')(error as any)
    }
  }, [ generateReport ])

  const schedules = schedulesData?.allReportSchedules?.edges ?? []
  const pageInfo = schedulesData?.allReportSchedules?.pageInfo
  const reportTypes = reportTypesData?.allReportTypes.edges ?? []

  const showPagination = pageInfo?.lastPage !== 1

  return (
    <div className="ReportsPage">
      <div className="page-header">
        <h1 className="u-heading">Управление отчетами</h1>
        <Button
          appearance="primary"
          onClick={handleCreateSchedule}
          disabled={loadingTypes}
        >
          Создать расписание
        </Button>
      </div>

      {error && (
        <Message
          showIcon
          type="error"
          header="При получении данных возникла ошибка!"
        >
          {error.message}
        </Message>
      )}

      {/* <QuickReportGeneration reportTypes={reportTypes} /> */}

      <Panel header="Расписания отчетов" bordered>
        <ReportSchedulesTable
          data={schedules}
          loading={loadingSchedules}
          onEdit={handleEditSchedule}
          onDelete={handleDeleteSchedule}
          onToggle={handleToggleSchedule}
          onGenerate={handleGenerateReport}
          sortColumn={sort.fieldName}
          sortType={sort.direction === 0 ? 'desc' : 'asc'}
          onSortColumn={handleSortColumn}
        />

        {showPagination && (
          <Pagination
            size="md"
            last
            first
            next
            prev
            ellipsis
            activePage={page}
            pages={pageInfo?.lastPage}
            total={pageInfo?.lastPage ?? 1}
            maxButtons={10}
            onSelect={handlePageSelect}
          />
        )}
      </Panel>

      <Modal
        open={showCreateModal}
        onClose={handleCloseModal}
        size="md"
      >
        <Modal.Header>
          <Modal.Title>
            {editingSchedule ? 'Редактировать расписание' : 'Создать расписание'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ReportScheduleForm
            reportTypes={reportTypes}
            initialData={editingSchedule}
            onSubmit={handleSubmitForm}
            onCancel={handleCloseModal}
            loading={savingSchedule}
          />
        </Modal.Body>
      </Modal>
    </div>
  )
}